import { CollisionPriority, type Modifiers } from '@dnd-kit/abstract';
import { useDroppable } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import { PlusIcon } from 'lucide-react';
import { ReactNode, Ref, useMemo, useRef } from 'react';

import { ChildConditionType, WorkflowScopeType } from '@invenco/common-domain/enums';

import { Button } from '@/components/core/button';

import { EditorConditionGroup } from '../editor-model';
import {
  addExistingLeaf,
  addLeafToGroup,
  deleteNode,
  newLeaf,
  type UpdateRule,
} from './condition-draft';
import { ConditionLeafRow, type ConditionLeafRowProps } from './condition-leaf-row';
import { columnModifiers, SortableGroupBlock, TreeReorderProvider } from './tree-reorder';

const connectiveLabel = (connective: ChildConditionType): string =>
  connective === ChildConditionType.ALL ? 'And' : 'Or';

/** Vertical rail with the connective chip centred on it. */
function ConditionRail({
  label,
  contentRef,
  children,
}: {
  label: string;
  contentRef?: Ref<HTMLDivElement>;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center self-stretch">
        <span className="w-px flex-1 bg-border" />
        <span className="my-1 rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
          {label}
        </span>
        <span className="w-px flex-1 bg-border" />
      </div>
      <div ref={contentRef} className="flex-1 space-y-2">
        {children}
      </div>
    </div>
  );
}

/** Wires a staged leaf row into the drag session under its container's id. */
function SortableLeafRow({
  index,
  container,
  modifiers,
  ...rowProps
}: Omit<ConditionLeafRowProps, 'rowRef' | 'handleRef'> & {
  index: number;
  container: string;
  modifiers: Modifiers;
}) {
  const { ref, handleRef } = useSortable({
    id: rowProps.leaf.id,
    index,
    group: container,
    modifiers,
  });
  return <ConditionLeafRow {...rowProps} rowRef={ref} handleRef={handleRef} />;
}

/**
 * A group's bordered box, doubling as the drag container a leaf enters. Low
 * collision priority: its own rows and the block's edge bands win over it, so
 * it only claims interior whitespace (inserting at the nearer end).
 */
function GroupRows({
  group,
  scopeType,
  incompleteLeafIds,
  updateRule,
  modifiers,
}: {
  group: EditorConditionGroup;
  scopeType: WorkflowScopeType;
  incompleteLeafIds: ReadonlySet<string>;
  updateRule: UpdateRule;
  modifiers: Modifiers;
}) {
  const { ref } = useDroppable({ id: group.id, collisionPriority: CollisionPriority.Low });
  return (
    <div ref={ref} className="space-y-2 rounded-lg border p-2">
      {group.children.map((child, index) =>
        child.kind === 'leaf' ? (
          <SortableLeafRow
            key={child.id}
            index={index}
            container={group.id}
            modifiers={modifiers}
            leaf={child}
            scopeType={scopeType}
            incomplete={incompleteLeafIds.has(child.id)}
            updateRule={updateRule}
            onDelete={() => updateRule((rule) => deleteNode(rule, child.id))}
          />
        ) : null,
      )}
      <Button
        type="button"
        variant="ghost"
        className="text-muted-foreground"
        onClick={() => updateRule((rule) => addLeafToGroup(rule, group.id, scopeType))}
      >
        <PlusIcon className="size-4" />
        Condition
      </Button>
    </div>
  );
}

interface ConditionGroupProps {
  root: EditorConditionGroup;
  scopeType: WorkflowScopeType;
  /** Ids of leaves the validator flagged incomplete. */
  incompleteLeafIds: ReadonlySet<string>;
  /** Stages a condition-draft recipe onto the rule being edited. */
  updateRule: UpdateRule;
}

/**
 * Groups never nest further; `tooDeep` rules never reach here. An empty
 * condition shows one presentational default row that isn't staged until the
 * user edits it, so opening the panel on a new rule leaves it clean.
 */
export function ConditionGroup({
  root,
  scopeType,
  incompleteLeafIds,
  updateRule,
}: ConditionGroupProps) {
  // Minted once so the unstaged empty-state row keeps its identity across renders.
  const placeholderLeaf = useMemo(() => newLeaf(scopeType), [scopeType]);
  const columnRef = useRef<HTMLDivElement>(null);
  const modifiers = columnModifiers(columnRef);

  // Empty-state edits stage the leaf and apply the edit in one atomic recipe;
  // once staged, the row is a normal working-copy leaf.
  const stageAndApply: UpdateRule = (recipe) =>
    updateRule((rule) => recipe(addExistingLeaf(rule, placeholderLeaf)));

  if (root.children.length === 0) {
    return (
      <ConditionRail label={connectiveLabel(root.connective)}>
        <ConditionLeafRow
          // Keyed by the pre-minted id so staging updates the row in place
          // instead of remounting it (an open popover survives).
          key={placeholderLeaf.id}
          leaf={placeholderLeaf}
          scopeType={scopeType}
          // unstaged, so the validator never sees it
          incomplete={false}
          updateRule={stageAndApply}
          // deleting the unstaged row must not stage it
          onDelete={() => {}}
        />
      </ConditionRail>
    );
  }

  return (
    <TreeReorderProvider root={root} updateRule={updateRule}>
      <ConditionRail label={connectiveLabel(root.connective)} contentRef={columnRef}>
        {root.children.map((child, index) =>
          child.kind === 'leaf' ? (
            <SortableLeafRow
              key={child.id}
              index={index}
              container={root.id}
              modifiers={modifiers}
              leaf={child}
              scopeType={scopeType}
              incomplete={incompleteLeafIds.has(child.id)}
              updateRule={updateRule}
              onDelete={() => updateRule((rule) => deleteNode(rule, child.id))}
            />
          ) : (
            // The group's rail spans the whole block, outside its bordered box.
            <SortableGroupBlock key={child.id} group={child} rootId={root.id} index={index}>
              <ConditionRail label={connectiveLabel(child.connective)}>
                <GroupRows
                  group={child}
                  scopeType={scopeType}
                  incompleteLeafIds={incompleteLeafIds}
                  updateRule={updateRule}
                  modifiers={modifiers}
                />
              </ConditionRail>
            </SortableGroupBlock>
          ),
        )}
      </ConditionRail>
    </TreeReorderProvider>
  );
}
